import React, { useState, useRef, useEffect, ReactNode, useCallback } from 'react';
import { safeRender } from '../domain/safe-render.js';
import type {
  ChatMessageViewModel,
  ComposerState,
  AttachmentRef,
  ImportStagingRecord,
} from '../domain/chat-contracts.js';
import { globalEntityLinkerRegistry, type TypeaheadSuggestion } from '../domain/entity-linker-registry.js';
import { useCurrentPrincipal, PrincipalProvider } from '../domain/principal-context.js';
import { defaultAttachmentService } from '../domain/attachment-service.js';
import { useHostContext } from '@herx/sdk';
import '../style.css';

// ─── Formatting ───────────────────────────────────────────────────────────────

export const RenderSafeText: React.FC<{ text: string }> = ({ text }) => {
  const segments = safeRender(text);

  const handleEntityClick = (type: string, id: string) => {
    window.dispatchEvent(
      new CustomEvent('open-entity', {
        detail: { entityType: type, entityId: id },
      }),
    );
  };

  const chipStyle = (bg: string, border: string, color: string): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    backgroundColor: bg,
    border: `1px solid ${border}`,
    color,
    borderRadius: '6px',
    padding: '1px 6px',
    margin: '0 2px',
    fontSize: '11px',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'monospace',
    lineHeight: 1.3,
    verticalAlign: 'baseline',
    transition: 'all 0.15s ease',
    outline: 'none',
  });

  return (
    <>
      {segments.map((seg, i) => {
        switch (seg.type) {
          case 'bold':
            return <strong key={i}>{seg.content}</strong>;
          case 'code':
            return (
              <code
                key={i}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '3px',
                  padding: '1px 5px',
                  fontFamily: 'monospace',
                  fontSize: '11px',
                }}
              >
                {seg.content}
              </code>
            );
          case 'link': {
            const isSafeUrl = seg.href?.startsWith('http://') || seg.href?.startsWith('https://');
            return (
              <a
                key={i}
                href={isSafeUrl ? seg.href : '#'}
                target={isSafeUrl ? '_blank' : undefined}
                rel="noopener noreferrer"
                style={{ color: '#60a5fa', textDecoration: 'underline' }}
              >
                {seg.content}
              </a>
            );
          }
          case 'taskbadge': {
            const taskId = seg.entityId || seg.content.replace('#', '');
            const titleText = `Task #${taskId}`;
            const statusText = 'in_progress';
            const tooltip = `#${taskId}: ${titleText}\n• Status: ${statusText}\n\n👉 Click to inspect live task context`;

            return (
              <button
                key={i}
                type="button"
                role="button"
                tabIndex={0}
                title={tooltip}
                aria-label={`Inspect Sprint Task #${taskId}: ${titleText}`}
                onClick={() => handleEntityClick('task', taskId)}
                style={chipStyle('rgba(56, 189, 248, 0.15)', 'rgba(56, 189, 248, 0.4)', '#38bdf8')}
              >
                <span>#{taskId}</span>
                <span style={{ fontSize: '9px', opacity: 0.8 }}>↗</span>
              </button>
            );
          }
          case 'dealbadge': {
            const dealId = seg.entityId || seg.content.replace('@deal-', '').replace('deal:', '');
            return (
              <button
                key={i}
                type="button"
                role="button"
                tabIndex={0}
                aria-label={`Inspect CRM Deal @deal-${dealId}`}
                onClick={() => handleEntityClick('deal', dealId)}
                style={chipStyle('rgba(52, 211, 153, 0.15)', 'rgba(52, 211, 153, 0.4)', '#34d399')}
              >
                <span>💼 @deal-{dealId}</span>
                <span style={{ fontSize: '9px', opacity: 0.8 }}>↗</span>
              </button>
            );
          }
          case 'leadbadge': {
            const leadId = seg.entityId || seg.content.replace('@lead-', '').replace('lead:', '');
            return (
              <button
                key={i}
                type="button"
                role="button"
                tabIndex={0}
                aria-label={`Inspect CRM Lead @lead-${leadId}`}
                onClick={() => handleEntityClick('lead', leadId)}
                style={chipStyle('rgba(167, 139, 250, 0.15)', 'rgba(167, 139, 250, 0.4)', '#c084fc')}
              >
                <span>🎯 @lead-{leadId}</span>
                <span style={{ fontSize: '9px', opacity: 0.8 }}>↗</span>
              </button>
            );
          }
          case 'usermention': {
            const userId = seg.entityId || seg.content.replace('@', '');
            return (
              <button
                key={i}
                type="button"
                role="button"
                tabIndex={0}
                aria-label={`View User Profile @${userId}`}
                onClick={() => handleEntityClick('user', userId)}
                style={chipStyle('rgba(96, 165, 250, 0.15)', 'rgba(96, 165, 250, 0.4)', '#60a5fa')}
              >
                <span>@{userId}</span>
              </button>
            );
          }
          case 'wikibadge': {
            const wikiUri = seg.entityId || seg.content;
            const tooltip = `📖 LLMWiki Runbook: ${wikiUri}\n\n👉 Click to view documentation & resolution steps`;
            return (
              <button
                key={i}
                type="button"
                role="button"
                tabIndex={0}
                title={tooltip}
                aria-label={`View Wiki Runbook: ${wikiUri}`}
                onClick={() => handleEntityClick('wiki', wikiUri)}
                style={chipStyle('rgba(251, 191, 36, 0.15)', 'rgba(251, 191, 36, 0.4)', '#fbbf24')}
              >
                <span>{wikiUri}</span>
                <span style={{ fontSize: '9px', opacity: 0.8 }}>📖</span>
              </button>
            );
          }
          case 'linebreak':
            return <br key={i} />;
          default:
            return <span key={i}>{seg.content}</span>;
        }
      })}
    </>
  );
};

export const CollapsibleText: React.FC<{ text: string; isLastInCluster: boolean; isSystem: boolean }> = ({ text, isLastInCluster, isSystem }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const clampLines = isLastInCluster ? 8 : 2;

  useEffect(() => {
    if (contentRef.current) {
      setIsOverflowing(contentRef.current.scrollHeight > contentRef.current.clientHeight);
    }
  }, [text, clampLines, isExpanded]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div
        ref={contentRef}
        style={{
          display: isExpanded ? 'block' : '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: isExpanded ? 'unset' : clampLines,
          overflow: isExpanded ? 'visible' : 'hidden',
          wordBreak: 'break-word',
        }}
      >
        <span>
          <RenderSafeText text={text} />
        </span>
      </div>
      {!isExpanded && isOverflowing && (
        <button
          onClick={() => setIsExpanded(true)}
          style={{
            alignSelf: 'flex-start',
            background: 'none',
            border: 'none',
            color: '#60a5fa',
            fontSize: 11,
            fontWeight: 600,
            cursor: 'pointer',
            padding: 0,
            marginTop: 2,
          }}
        >
          ... Show more
        </button>
      )}
      {isExpanded && (
        <button
          onClick={() => setIsExpanded(false)}
          style={{
            alignSelf: 'flex-start',
            background: 'none',
            border: 'none',
            color: '#94a3b8',
            fontSize: 11,
            fontWeight: 600,
            cursor: 'pointer',
            padding: 0,
            marginTop: 4,
          }}
        >
          Show less
        </button>
      )}
    </div>
  );
};

// ─── Attachment Bubble Item ──────────────────────────────────────────────────

export const AttachmentBubbleItem: React.FC<{
  attachment: AttachmentRef;
  onOpenLightbox?: (url: string, name: string) => void;
}> = ({ attachment, onOpenLightbox }) => {
  const [objectUrl, setObjectUrl] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    if (attachment.previewable) {
      setLoading(true);
      defaultAttachmentService.resolveObjectUrl(attachment).then((url) => {
        if (mounted) {
          setObjectUrl(url);
          setLoading(false);
        }
      });
    }
    return () => {
      mounted = false;
    };
  }, [attachment]);

  const handleDownload = async () => {
    const url = objectUrl || (await defaultAttachmentService.resolveObjectUrl(attachment));
    if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.name;
      a.click();
    }
  };

  if (attachment.previewable) {
    return (
      <div style={{ marginTop: 6, borderRadius: 8, overflow: 'hidden', maxWidth: 280 }}>
        {loading ? (
          <div style={{ padding: '20px', background: 'rgba(0,0,0,0.2)', fontSize: 11, color: '#94a3b8' }}>
            Loading encrypted preview...
          </div>
        ) : objectUrl ? (
          <img
            src={objectUrl}
            alt={attachment.name}
            onClick={() => onOpenLightbox?.(objectUrl, attachment.name)}
            style={{ width: '100%', maxHeight: 200, objectFit: 'cover', display: 'block', cursor: 'pointer', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)' }}
          />
        ) : (
          <div style={{ padding: '8px', fontSize: 11, color: '#f87171' }}>Preview unavailable</div>
        )}
      </div>
    );
  }

  // Document file chip
  return (
    <div
      onClick={handleDownload}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        marginTop: 6,
        background: 'rgba(0,0,0,0.25)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 8,
        fontSize: 12,
        color: '#93c5fd',
        cursor: 'pointer',
      }}
      title="Click to download attachment"
    >
      <span>📄</span>
      <span style={{ fontWeight: 600, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {attachment.name}
      </span>
      <span style={{ fontSize: 10, color: '#94a3b8' }}>
        ({(attachment.sizeBytes / 1024).toFixed(0)} KB)
      </span>
      <span style={{ fontSize: 10 }}>⬇️</span>
    </div>
  );
};

// ─── Message Bubble ───────────────────────────────────────────────────────────

export const MessageBubble: React.FC<{
  message: ChatMessageViewModel;
  currentPrincipalId?: string;
  accentColor?: string;
  trailingContent?: ReactNode;
  onReply?: (eventId: string) => void;
  onOpenProfile?: (authorId: string) => void;
  onDelete?: (eventId: string) => void;
  onOpenLightbox?: (url: string, name: string) => void;
}> = ({
  message,
  currentPrincipalId,
  accentColor = '#3b82f6',
  trailingContent,
  onReply,
  onOpenProfile,
  onDelete,
  onOpenLightbox,
}) => {
  const principal = useCurrentPrincipal();
  const isHuman = message.authorType === 'human';
  const authorId = message.authorPrincipalId ?? message.authorId;
  const isMine = message.isMine !== undefined
    ? message.isMine
    : (currentPrincipalId ? authorId === currentPrincipalId : principal.isSelf(message.authorPrincipalId, message.authorDisplayName, authorId));
  const isSystem = message.authorType === 'system';
  const isLastInCluster = message.clusterPosition === 'last' || message.clusterPosition === 'single';

  if (isSystem) {
    return (
      <div style={{ textAlign: 'center', margin: '8px 0', width: '100%' }}>
        <span style={{ fontSize: 10, color: '#475569', background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '2px 10px' }}>
          {message.body}
        </span>
      </div>
    );
  }

  return (
    <article
      data-renderer="SHARED-CHAT-ACTIVE-2026-08-16"
      data-message-id={message.eventId}
      data-message-direction={isMine ? 'outgoing' : 'incoming'}
      aria-label={`Message from ${message.authorDisplayName}`}
      className={`chat-message ${isMine ? 'chat-message--outgoing' : 'chat-message--incoming'}`}
      style={{
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
        flexDirection: isMine ? 'row-reverse' : 'row',
        alignSelf: isMine ? 'flex-end' : 'flex-start',
        width: 'fit-content',
        maxWidth: '100%',
        padding: '4px 12px',
      }}
    >
      <div className="chat-message__avatar" aria-hidden="true" style={{ width: '32px', height: '32px', borderRadius: '50%', background: isHuman ? 'rgba(99,102,241,0.25)' : accentColor + '22', border: '1px solid ' + (isHuman ? '#6366f1' : accentColor) + '44', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>
        {message.authorAvatar || (isHuman ? '👤' : '🤖')}
      </div>
      <div className="chat-message__content" style={{ display: 'flex', flexDirection: 'column', gap: '2px', maxWidth: 'min(78vw, 720px)', minWidth: 0 }}>
        <div className="chat-message__meta" style={{ fontSize: '10px', color: '#64748b', fontWeight: 500, textAlign: isMine ? 'right' : 'left', display: 'flex', alignItems: 'center', gap: 6, justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
          <span>{message.authorDisplayName}</span>
          <span> · </span>
          <time dateTime={new Date(message.createdAt).toISOString()}>
            {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </time>
          {isMine && onDelete && (
            <button
              onClick={() => onDelete(message.eventId)}
              title="Delete message"
              style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 10, padding: 0 }}
            >
              ✕
            </button>
          )}
        </div>
        
        <div
          className="chat-message__bubble"
          style={{
            padding: '10px 14px',
            fontSize: '13px',
            lineHeight: 1.5,
            color: '#e2e8f0',
            overflowWrap: 'anywhere',
            background: isMine ? 'rgba(99,102,241,0.18)' : 'rgba(255,255,255,0.05)',
            border: isMine ? '1px solid rgba(99,102,241,0.35)' : '1px solid rgba(255,255,255,0.08)',
            borderRadius: isMine ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
          }}
        >
          <CollapsibleText text={message.body} isLastInCluster={isMine ? true : isLastInCluster} isSystem={isSystem} />
          
          {/* Attachments rendering */}
          {message.attachments && message.attachments.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
              {message.attachments.map((att) => (
                <AttachmentBubbleItem key={att.attachmentId} attachment={att} onOpenLightbox={onOpenLightbox} />
              ))}
            </div>
          )}

          {trailingContent && (
            <div style={{ marginTop: 8 }}>
              {trailingContent}
            </div>
          )}
        </div>
        
        {message.agent && (
          <div style={{ fontSize: 10, color: '#475569', display: 'flex', gap: 8, marginTop: 4 }}>
            <span>✨ AI Generated</span>
            {message.agent.confidence != null && (
              <span style={{ color: message.agent.confidence >= 0.8 ? '#34d399' : '#f59e0b' }}>
                · {Math.round(message.agent.confidence * 100)}% confidence
              </span>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

// ─── Main Shared Layout ───────────────────────────────────────────────────────

export interface SharedChatUIProps {
  messages: readonly ChatMessageViewModel[];
  composer: ComposerState;
  onSend: (text: string, attachments?: readonly AttachmentRef[]) => void;
  onRetry?: (eventId: string) => void;
  onReply?: (eventId: string) => void;
  onDelete?: (eventId: string) => void;
  onClearHistory?: () => void;
  onExport?: () => void;
  onStageImport?: (file: File) => void;
  stagedImport?: ImportStagingRecord | null;
  onConfirmImport?: () => void;
  onCancelImport?: () => void;
  onOpenProfile?: (authorId: string) => void;
  messageAccessory?: (message: ChatMessageViewModel) => ReactNode;
  header?: ReactNode;
  sidebar?: ReactNode;
  beforeComposer?: ReactNode;
  accentColor?: string;
  currentPrincipalId?: string;
}

export const SharedChatUI: React.FC<SharedChatUIProps> = ({
  messages,
  composer,
  onSend,
  onReply,
  onDelete,
  onClearHistory,
  onExport,
  onStageImport,
  stagedImport,
  onConfirmImport,
  onCancelImport,
  onOpenProfile,
  messageAccessory,
  header,
  sidebar,
  beforeComposer,
  accentColor = '#3b82f6',
  currentPrincipalId,
}) => {
  const [text, setText] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<AttachmentRef[]>([]);
  const [isAttaching, setIsAttaching] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [lightbox, setLightbox] = useState<{ url: string; name: string } | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importInputRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  let host: any = {};
  try {
    host = useHostContext();
  } catch (e) {
    // Fallback if rendered outside of a module host context (e.g., inside the shell directly)
    host = {
      events: {
        emit: (event: string, payload: any) => {
          window.dispatchEvent(new CustomEvent(event, { detail: payload }));
        }
      }
    };
  }

  // Typeahead State (WAI-ARIA Combobox)
  const [typeaheadSuggestions, setTypeaheadSuggestions] = useState<TypeaheadSuggestion[]>([]);
  const [selectedTypeaheadIndex, setSelectedTypeaheadIndex] = useState<number>(0);
  const [typeaheadTrigger, setTypeaheadTrigger] = useState<string | null>(null);
  const [typeaheadQuery, setTypeaheadQuery] = useState<string>('');
  const [isComposing, setIsComposing] = useState(false);
  const typeaheadAbortRef = useRef<AbortController | null>(null);
  const requestSeqRef = useRef<number>(0);

  // Auto-scroll on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Query typeahead when text or cursor changes (unless IME composing)
  const handleTextChange = (val: string) => {
    setText(val);

    if (isComposing) return;

    // Detect trailing trigger token: e.g. "I am working on #8" -> trigger = '#', query = '8'
    const match = val.match(/(?:^|\s)(#|@)([a-zA-Z0-9_-]*)$/);
    if (!match) {
      if (typeaheadAbortRef.current) {
        typeaheadAbortRef.current.abort();
        typeaheadAbortRef.current = null;
      }
      setTypeaheadSuggestions([]);
      setTypeaheadTrigger(null);
      setTypeaheadQuery('');
      return;
    }

    const trigger = match[1];
    const query = match[2] || '';

    // Abort previous in-flight query
    if (typeaheadAbortRef.current) {
      typeaheadAbortRef.current.abort();
    }

    const controller = new AbortController();
    typeaheadAbortRef.current = controller;
    const currentSeq = ++requestSeqRef.current;

    globalEntityLinkerRegistry
      .getTypeaheadSuggestions(
        trigger,
        query,
        { meshId: 'local', workspaceId: 'default' },
        controller.signal,
      )
      .then((results) => {
        if (requestSeqRef.current === currentSeq && !controller.signal.aborted) {
          setTypeaheadSuggestions(results);
          setSelectedTypeaheadIndex(0);
          setTypeaheadTrigger(trigger);
          setTypeaheadQuery(query);
        }
      })
      .catch(() => {
        // Ignored
      });
  };

  const handleSelectSuggestion = (suggestion: TypeaheadSuggestion) => {
    if (!typeaheadTrigger) return;

    // Replace the trailing trigger token with the suggestion's canonical token
    const regex = new RegExp(`(?:^|\\s)${typeaheadTrigger}${typeaheadQuery}$`);
    const match = text.match(regex);
    if (match) {
      const startIdx = text.lastIndexOf(match[0]);
      const prefix = text.substring(0, startIdx) + (match[0].startsWith(' ') ? ' ' : '');
      const newText = prefix + suggestion.token + ' ';
      setText(newText);
    } else {
      setText((prev) => `${prev} ${suggestion.token} `);
    }

    // Dismiss typeahead and refocus textarea
    setTypeaheadSuggestions([]);
    setTypeaheadTrigger(null);
    setTypeaheadQuery('');
    if (typeaheadAbortRef.current) {
      typeaheadAbortRef.current.abort();
      typeaheadAbortRef.current = null;
    }
    textareaRef.current?.focus();
  };

  const handleComposerBlur = () => {
    // Focus & Blur Boundary Observer: unmount and reset active descendant
    setTimeout(() => {
      if (typeaheadAbortRef.current) {
        typeaheadAbortRef.current.abort();
        typeaheadAbortRef.current = null;
      }
      setTypeaheadSuggestions([]);
      setTypeaheadTrigger(null);
    }, 150);
  };

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    setIsAttaching(true);
    const added: AttachmentRef[] = [];
    for (let i = 0; i < files.length; i++) {
      try {
        const ref = await defaultAttachmentService.stageAndEncryptFile(files[i]);
        added.push(ref);
      } catch (err: any) {
        alert(err.message || 'Attachment error');
      }
    }
    setPendingAttachments((prev) => [...prev, ...added]);
    setIsAttaching(false);
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleSendAction = () => {
    const trimmed = text.trim();
    
    if (trimmed.startsWith('/todo ')) {
      const title = trimmed.replace('/todo ', '');
      host.events?.emit('task:create', { title, sourceModule: 'chat' });
      setText('');
      return;
    }
    if (trimmed === '/standup') {
      host.events?.emit('scrum:standup:trigger', { channelId: 'current' });
      setText('');
      return;
    }

    if ((trimmed || pendingAttachments.length > 0) && composer !== 'composing_ime') {
      onSend(trimmed, pendingAttachments.length > 0 ? pendingAttachments : undefined);
      setText('');
      setPendingAttachments([]);
    }
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
      style={{ display: 'flex', flex: 1, overflow: 'hidden', height: '100%', backgroundColor: 'var(--theme-bg-main, #0f172a)', color: '#f1f5f9', position: 'relative' }}
    >
      {/* Drag overlay */}
      {isDragOver && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(59,130,246,0.3)', backdropFilter: 'blur(4px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '3px dashed #60a5fa', pointerEvents: 'none' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>📁 Drop files here to attach</div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
        >
          <img src={lightbox.url} alt={lightbox.name} style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 12 }} />
        </div>
      )}

      {/* Staged Import Confirmation Modal */}
      {stagedImport && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)', zIndex: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 16, padding: 24, maxWidth: 480, width: '100%', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#f8fafc' }}>📥 Confirm Chat Backup Import</h3>
            <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.6 }}>
              <div><strong>Format:</strong> {stagedImport.packageData?.manifest.format} (v{stagedImport.packageData?.manifest.version})</div>
              <div><strong>Accepted Messages:</strong> <span style={{ color: '#34d399', fontWeight: 700 }}>{stagedImport.report.acceptedMessages}</span></div>
              <div><strong>Duplicate Messages:</strong> {stagedImport.report.duplicateMessages}</div>
              <div><strong>Rejected Messages:</strong> {stagedImport.report.rejectedMessages}</div>
              {stagedImport.report.conflicts.length > 0 && (
                <div style={{ marginTop: 8, padding: 8, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, fontSize: 11, color: '#f87171' }}>
                  {stagedImport.report.conflicts.join(', ')}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
              <button onClick={onCancelImport} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
              <button onClick={onConfirmImport} style={{ padding: '8px 18px', background: '#3b82f6', border: 'none', color: '#fff', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Commit Import ✓</button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      {sidebar}

      {/* Main Chat Feed */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        
        {/* Header */}
        <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>{header}</div>
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowMenu((prev) => !prev)} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#cbd5e1', borderRadius: 6, padding: '4px 8px', fontSize: 12, cursor: 'pointer' }}>
              ⚙️ Options ▾
            </button>
            {showMenu && (
              <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, background: '#1e293b', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: '6px 0', minWidth: 180, zIndex: 50, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column' }}>
                {onExport && (
                  <button onClick={() => { setShowMenu(false); onExport(); }} style={{ padding: '8px 14px', background: 'none', border: 'none', color: '#f8fafc', fontSize: 12, textAlign: 'left', cursor: 'pointer' }}>
                    📤 Export Chat History
                  </button>
                )}
                {onStageImport && (
                  <button onClick={() => { setShowMenu(false); importInputRef.current?.click(); }} style={{ padding: '8px 14px', background: 'none', border: 'none', color: '#f8fafc', fontSize: 12, textAlign: 'left', cursor: 'pointer' }}>
                    📥 Import Messages
                  </button>
                )}
                {onClearHistory && (
                  <button onClick={() => { setShowMenu(false); if (confirm('Clear local history for this room?')) onClearHistory(); }} style={{ padding: '8px 14px', background: 'none', border: 'none', color: '#f87171', fontSize: 12, textAlign: 'left', cursor: 'pointer' }}>
                    🧹 Clear Local Cache
                  </button>
                )}
              </div>
            )}
            <input
              ref={importInputRef}
              type="file"
              accept=".json"
              style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  onStageImport?.(e.target.files[0]);
                }
              }}
            />
          </div>
        </div>

        {/* Message Feed */}
        <div role="log" aria-live="polite" aria-label="Conversation messages" style={{ flex: 1, overflowY: 'auto', padding: '16px 0', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
          {messages.map((msg) => (
            <MessageBubble
              key={msg.eventId}
              message={msg}
              currentPrincipalId={currentPrincipalId}
              accentColor={accentColor}
              onReply={onReply}
              onDelete={onDelete}
              onOpenProfile={onOpenProfile}
              onOpenLightbox={(url, name) => setLightbox({ url, name })}
              trailingContent={messageAccessory ? messageAccessory(msg) : null}
            />
          ))}
          <div ref={bottomRef} />
        </div>

        {/* Composer Area */}
        {beforeComposer}
        <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          
          {/* Attachment Tray */}
          {pendingAttachments.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {pendingAttachments.map((att) => (
                <div key={att.attachmentId} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, padding: '3px 8px', fontSize: 11, color: '#93c5fd' }}>
                  <span>📎</span>
                  <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.name}</span>
                  <button onClick={() => setPendingAttachments((prev) => prev.filter((a) => a.attachmentId !== att.attachmentId))} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 12, padding: 0 }}>✕</button>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isAttaching}
              title="Attach files (images, docs)"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', borderRadius: '8px', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16 }}
            >
              📎
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files) handleFiles(e.target.files);
              }}
            />

            <div style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column' }}>
              {/* Typeahead Suggestions Popover */}
              {typeaheadSuggestions.length > 0 && (
                <div
                  id="typeahead-listbox"
                  role="listbox"
                  aria-label="Entity suggestions"
                  style={{
                    position: 'absolute',
                    bottom: 'calc(100% + 8px)',
                    left: 0,
                    right: 0,
                    backgroundColor: '#0b0f19',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '12px',
                    boxShadow: '0 12px 36px rgba(0, 0, 0, 0.85), 0 0 14px rgba(56, 189, 248, 0.15)',
                    maxHeight: '220px',
                    overflowY: 'auto',
                    zIndex: 100,
                    padding: '6px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}
                >
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', padding: '2px 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Suggestions matching {typeaheadTrigger}
                  </div>
                  {typeaheadSuggestions.map((item, idx) => {
                    const isSelected = idx === selectedTypeaheadIndex;
                    return (
                      <div
                        key={item.id}
                        id={`typeahead-opt-${idx}`}
                        role="option"
                        aria-selected={isSelected}
                        onMouseDown={(e) => {
                          e.preventDefault(); // Prevent blurring textarea
                          handleSelectSuggestion(item);
                        }}
                        onMouseEnter={() => setSelectedTypeaheadIndex(idx)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '7px 10px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          backgroundColor: isSelected ? 'rgba(56, 189, 248, 0.15)' : 'transparent',
                          border: isSelected ? '1px solid rgba(56, 189, 248, 0.35)' : '1px solid transparent',
                          color: isSelected ? '#38bdf8' : '#cbd5e1',
                          transition: 'all 0.1s ease',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '14px' }}>{item.icon || '🏷️'}</span>
                          <div>
                            <span style={{ fontWeight: 600, color: isSelected ? '#38bdf8' : '#f1f5f9' }}>
                              {item.displayLabel}
                            </span>
                            {item.secondaryText && (
                              <span style={{ fontSize: '10px', color: '#64748b', marginLeft: '6px', fontFamily: 'monospace' }}>
                                {item.secondaryText}
                              </span>
                            )}
                          </div>
                        </div>

                        {item.category && (
                          <span style={{ fontSize: '9px', textTransform: 'uppercase', padding: '1px 6px', borderRadius: '4px', backgroundColor: 'rgba(255, 255, 255, 0.05)', color: '#94a3b8' }}>
                            {item.category}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <textarea
                data-testid="chat-composer-input"
                ref={textareaRef}
                value={text}
                onChange={(e) => handleTextChange(e.target.value)}
                onBlur={handleComposerBlur}
                onCompositionStart={() => setIsComposing(true)}
                onCompositionEnd={() => setIsComposing(false)}
                onKeyDown={(e) => {
                  if (typeaheadSuggestions.length > 0) {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setSelectedTypeaheadIndex((prev) => (prev + 1) % typeaheadSuggestions.length);
                      return;
                    }
                    if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setSelectedTypeaheadIndex((prev) => (prev - 1 + typeaheadSuggestions.length) % typeaheadSuggestions.length);
                      return;
                    }
                    if (e.key === 'Enter' || e.key === 'Tab') {
                      e.preventDefault();
                      handleSelectSuggestion(typeaheadSuggestions[selectedTypeaheadIndex]);
                      return;
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setTypeaheadSuggestions([]);
                      setTypeaheadTrigger(null);
                      return;
                    }
                  }

                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendAction();
                  }
                }}
                placeholder="Type a message or use # for tasks, @ for members & deals..."
                role="combobox"
                aria-expanded={typeaheadSuggestions.length > 0}
                aria-controls="typeahead-listbox"
                aria-autocomplete="list"
                aria-activedescendant={
                  typeaheadSuggestions.length > 0 ? `typeahead-opt-${selectedTypeaheadIndex}` : undefined
                }
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  color: '#fff',
                  fontSize: '13px',
                  minHeight: '40px',
                  maxHeight: '120px',
                  resize: 'none',
                  fontFamily: 'inherit',
                  outline: 'none',
                }}
                disabled={composer === 'sending' || composer === 'streaming'}
              />
            </div>

            <button
              onClick={handleSendAction}
              disabled={(!text.trim() && pendingAttachments.length === 0) || composer === 'sending' || composer === 'streaming'}
              style={{
                background: (text.trim() || pendingAttachments.length > 0) ? '#3b82f6' : 'rgba(59,130,246,0.3)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '0 16px',
                height: 40,
                fontWeight: 600,
                cursor: (text.trim() || pendingAttachments.length > 0) ? 'pointer' : 'not-allowed',
                transition: 'background 0.2s',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: '2px' }}>
                <path d="m22 2-7 20-4-9-9-4Z"/>
                <path d="M22 2 11 13"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { PrincipalProvider, useCurrentPrincipal } from '../domain/principal-context.js';
